// Syndication packs (1.56), the search-demand map (1.34) and the AI search visibility check (1.37): the shapes the
// Newsletter and Content pages read (migration 20261101140000: crm_syndication_packs, crm_search_reports) and the small
// pure helpers they use. Ogma never posts a pack: the owner pastes it and marks it posted with its address.

export type SyndicationTarget = "linkedin" | "substack" | "medium" | "trade_press";

export const TARGET_LABEL: Record<SyndicationTarget, string> = {
  linkedin: "LinkedIn newsletter",
  substack: "Substack",
  medium: "Medium",
  trade_press: "Trade press pitch",
};

export interface SyndicationPackBody {
  title: string;
  body: string;
  format: "html" | "markdown" | "text";
  canonical_url: string;
  canonical_note: string;
  cover_image?: { url?: string; storage_path?: string; alt?: string | null } | null;
  cta?: { code?: string; label?: string; href?: string } | null;
  site?: string;
  target_label?: string;
  how_to?: string;
  pitch_id?: string;
}

export interface SyndicationPack {
  task_id: string;
  content_item_id: string;
  issue_title: string | null;
  content_kind: string | null;
  issue_published_at: string | null;
  target: SyndicationTarget;
  status: "ready" | "posted" | "done";
  pack: SyndicationPackBody;
  posted_url: string | null;
  posted_at: string | null;
  created_at: string;
}

export type DemandIntent = "problem" | "comparison" | "price" | "local" | "how_to";

export const INTENT_LABEL: Record<DemandIntent, string> = {
  problem: "Problem",
  comparison: "Comparison",
  price: "Price",
  local: "Local",
  how_to: "How-to",
};
export const INTENTS: DemandIntent[] = ["problem", "comparison", "price", "local", "how_to"];

export interface DemandQuestion {
  id: string;
  question: string;
  intent: DemandIntent;
  angle: string;
  offer: string;
  answered: boolean;
  article_id: string | null;
  volume: number | null;
}

export interface VisibilityResult {
  provider: string;
  question: string;
  mentioned?: boolean;
  cited?: boolean;
  competitors?: string[];
  cited_hosts?: string[];
  error?: string;
}

export interface Hygiene {
  checked: boolean;
  reason?: string;
  site?: string;
  sitemap?: boolean;
  llms_txt?: boolean;
  structured_data?: string[];
  blocked?: string[];
  findings?: string[];
}

export interface ReportDoc<M> {
  id: string;
  title: string;
  version: number;
  created_at: string;
  body: string | null;
  metadata: M;
}

export interface DemandMeta {
  month?: string;
  service_area?: string;
  volumes?: string;
  volumes_from?: string;
  questions?: DemandQuestion[];
}

export interface VisibilityMeta {
  checked_on?: string;
  configured?: boolean;
  providers?: string[];
  questions?: string[];
  results?: VisibilityResult[];
  summary?: Record<string, { asked: number; mentioned: number; cited: number }>;
  hygiene?: Hygiene;
  note?: string;
}

export interface SearchReports {
  demand_map: ReportDoc<DemandMeta> | null;
  visibility: ReportDoc<VisibilityMeta> | null;
}

/** The map's questions by intent, in the fixed intent order; empty intents left out. */
export function byIntent(questions: DemandQuestion[]): { intent: DemandIntent; questions: DemandQuestion[] }[] {
  return INTENTS.map((intent) => ({ intent, questions: questions.filter((q) => q.intent === intent) })).filter((g) => g.questions.length > 0);
}

/** How many of the map's questions an article already answers. */
export function answeredCount(questions: DemandQuestion[]): { answered: number; total: number } {
  return { answered: questions.filter((q) => q.answered).length, total: questions.length };
}

/** A pack as plain text, for pasting where rich text is not taken (and for the text clipboard alongside HTML). */
export function packPlainText(pack: SyndicationPackBody): string {
  if (pack.format !== "html") return pack.body;
  return pack.body
    .replace(/<a\s[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gis, "$2 ($1)")
    .replace(/<(br|\/p|\/div|\/h[1-6]|\/li|hr)\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;|&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** True for the public https address of a post: what "Mark posted" accepts (the database checks it again). */
export function isPostUrl(value: string): boolean {
  return /^https:\/\/[^\s]+$/.test(value.trim()) && value.trim().length <= 500;
}
